import type { KaiAgentState } from "../Components/KaiAgentStatus/KaiAgentStatus";

/** The machine-readable state the server sends next to `isThinking` on `bot.writting`. */
export type BotWritingState = "thinking" | "fetching" | "listening";

/** A `bot.tool` event says a tool just ran; the cog shows for this long. */
export const TOOL_BLIP_MS = 1500;
/** The check shows for this long after the bot's turn ends, then the face is back. */
export const DONE_HOLD_MS = 1800;

// Composer-type actions (the AI flow itself) never ask the customer to act;
// every other action type does (buttons, inputs, ratings) — same list
// Conversation.tsx uses to yield the waiting indicator.
const COMPOSER_ACTION_TYPES = ["answerbotflow", "aitask", "chatinput", "passtoagent"];

export interface KaiAgentStateInput {
  botWrittingText?: string;
  botWrittingIsThinking?: boolean;
  botWrittingState?: BotWritingState;
  /** `Date.now()` of the last `bot.tool` event. */
  botToolAt?: number;
  /** `Date.now()` when the last bot turn ended (its final comment landed). */
  botTurnEndedAt?: number;
  /** The row is a typing / pending-thinking placeholder. */
  typing?: boolean;
  /**
   * The row is a settled answer (the latest one): it carries what the
   * conversation needs from the customer or how the turn ended, never the
   * streaming states — those belong to the placeholder row below it.
   */
  settled?: boolean;
  currentBotActionType?: string;
}

/**
 * What the Kai avatar shows for a live message row. Pure, so it is covered
 * by KaiAgentState.test.ts; the hook in useKaiAgentState.ts feeds it the
 * store. Priority, top wins:
 *   handed to a person → paused; an action the customer must answer →
 *   attention; a tool just ran → tools; a status line → fetching / thinking;
 *   answer text streaming → working; a typing placeholder → idle (the bubble
 *   already shows the dots, so the avatar stays the Kai face); the turn just
 *   ended → done; otherwise idle.
 */
export const deriveKaiAgentState = (
  input: KaiAgentStateInput,
  nowMs: number,
): KaiAgentState => {
  const actionType = input.currentBotActionType;
  if (actionType === "passtoagent") return "paused";
  if (actionType && !COMPOSER_ACTION_TYPES.includes(actionType)) return "attention";
  if (input.settled) {
    if (input.botTurnEndedAt && nowMs - input.botTurnEndedAt < DONE_HOLD_MS) return "done";
    return "idle";
  }
  if (input.botToolAt && nowMs - input.botToolAt < TOOL_BLIP_MS) return "tools";
  if (input.botWrittingIsThinking) {
    return input.botWrittingState === "fetching" ? "fetching" : "thinking";
  }
  if (input.botWrittingText && input.botWrittingText.length > 0) return "working";
  if (input.typing) return "idle";
  if (input.botTurnEndedAt && nowMs - input.botTurnEndedAt < DONE_HOLD_MS) return "done";
  return "idle";
};
